// EmotionalRhythmTab.tsx
import React, { useState } from "react";
import { Waves } from "lucide-react";
import AstriaUKV2Shell from "../AstriaUKV2Shell";
import { useUKV2Colors } from "../AstriaUKV2ThemeContext";
import {
  AstriaUKV2ResultCard,
  AstriaUKV2FieldBlock,
  UKV2LoadingState,
  UKV2ErrorState,
  UKV2ChatCta,
} from "../AstriaUKV2Card";
import { UKV2TextInput, UKV2SubmitButton } from "../AstriaUKV2Wizard";
import { useAstriaUKV2Tab } from "../useAstriaUKV2Tab";
import { AstriaUKV2EmotionalRhythmData, Message } from "../../../../contexts/ChatContext";

const SUBCATEGORY_NAME = "Emotional Rhythm";

// Colors must be read INSIDE AstriaUKV2Shell's subtree (see the note in
// ZodiacPersonalityTab.tsx) — these small components are rendered as
// `children`, not evaluated inline, so their hook calls resolve after the
// shell's theme provider has mounted.

const ResultView: React.FC<{
  data?: AstriaUKV2EmotionalRhythmData;
  message: Message | null;
  reset: () => void;
  goToChat: () => Promise<void>;
}> = ({ data, message, reset, goToChat }) => {
  const { colors } = useUKV2Colors();

  if (data) {
    return (
      <>
        <AstriaUKV2ResultCard
          icon={<Waves className="w-5 h-5" />}
          title="Emotional Rhythm"
          subtitle="Your pacing through the day"
          shareText={`${data.morning}\n\n${data.night}`}
        >
          <AstriaUKV2FieldBlock label="Morning" value={data.morning} />
          <AstriaUKV2FieldBlock label="Midday" value={data.midday} />
          <AstriaUKV2FieldBlock label="Afternoon" value={data.afternoon} />
          <AstriaUKV2FieldBlock label="Evening" value={data.evening} />
          <AstriaUKV2FieldBlock label="Night" value={data.night} />
        </AstriaUKV2ResultCard>
        <button
          onClick={reset}
          className="text-xs font-semibold transition-colors"
          style={{ marginTop: 14, color: colors.mutedColor }}
        >
          Start a New Reading
        </button>
        <UKV2ChatCta onClick={goToChat} />
      </>
    );
  }

  if (message) {
    return (
      <AstriaUKV2ResultCard icon={<Waves className="w-5 h-5" />} title="Emotional Rhythm">
        <p
          style={{
            fontSize: 15,
            lineHeight: 1.75,
            color: colors.bodyColor,
            whiteSpace: "pre-wrap",
          }}
        >
          {message.content}
        </p>
      </AstriaUKV2ResultCard>
    );
  }

  return null;
};

const IntroForm: React.FC<{
  dob: string;
  setDob: (v: string) => void;
  onSubmit: () => void;
}> = ({ dob, setDob, onSubmit }) => {
  const { colors } = useUKV2Colors();

  return (
    <>
      <div style={{ marginBottom: 20 }}>
        <h2
          style={{
            fontSize: 21,
            fontWeight: 700,
            color: colors.titleColor,
            marginBottom: 6,
          }}
        >
          Emotional Rhythm
        </h2>
        <p style={{ fontSize: 13.5, color: colors.mutedColor, lineHeight: 1.6 }}>
          A finer-grained look at how your emotional energy moves — morning through to night.
        </p>
      </div>

      <div style={{ marginBottom: 20 }}>
        <UKV2TextInput value={dob} onChange={setDob} placeholder="Date of birth (DD/MM/YYYY)" />
      </div>

      <UKV2SubmitButton onClick={onSubmit} disabled={!dob.trim()} label="Reveal My Rhythm" />
    </>
  );
};

const EmotionalRhythmTab: React.FC = () => {
  const { loading, error, message, send, reset, goToChat } = useAstriaUKV2Tab({
    subCategoryName: SUBCATEGORY_NAME,
  });

  const [dob, setDob] = useState("");
  const started = !!message || loading || !!error;

  const handleSubmit = () =>
    send(`Give me my Emotional Rhythm for today. My Date of Birth: ${dob.trim()}.`);

  const data = message?.astriaUKV2Data as AstriaUKV2EmotionalRhythmData | undefined;

  if (started) {
    return (
      <AstriaUKV2Shell active="emotional-rhythm">
        {loading && <UKV2LoadingState label="Tracking how your energy moves…" />}
        {!loading && error && <UKV2ErrorState message={error} onRetry={handleSubmit} />}
        {!loading && !error && (
          <ResultView data={data} message={message} reset={reset} goToChat={goToChat} />
        )}
      </AstriaUKV2Shell>
    );
  }

  return (
    <AstriaUKV2Shell active="emotional-rhythm">
      <IntroForm dob={dob} setDob={setDob} onSubmit={handleSubmit} />
    </AstriaUKV2Shell>
  );
};

export default EmotionalRhythmTab;
